import axios from "axios";
import { action, makeObservable, observable, runInAction } from "mobx";
import NewOperationsDashboardAPI from "../../api/dashboards/NewOperationsDashboardAPI";
import { trackOpsTatAction } from "../../../../analytics/opsTatTracking";
import {
  defaultOpsTatAppliedFilters,
  defaultOpsTatDrilldownQuery,
  resolveOpsInwardDevPageSize,
  OpsTatAppliedFilters,
  OpsTatDrilldownQuery,
  OpsTatPath,
  toOpsAnalyticsFilters,
} from "../../../../models/Dashboard/OpsTat/OpsTatFilters";
import {
  OpsDashboardMetricId,
  OpsTatDrilldownResult,
} from "../../../../models/Dashboard/OpsTat/OpsTatDrilldown";
import { OpsTatSummaryResult } from "../../../../models/Dashboard/OpsTat/OpsTatSummary";
import {
  OpsInwardDevSummaryResult,
  OpsInwardDevVendorsResult,
} from "../../../../models/Dashboard/OpsTat/OpsInwardDeviation";

export type OpsDrawerSource = "tat" | "inward_dev";

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string" &&
      data.message
    ) {
      return data.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const triggerCsvDownload = (blob: Blob, filename?: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename || "ops-tat-export.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

export class NewOperationsDashboardStore {
  analyticsAPI: NewOperationsDashboardAPI;
  appliedFilters: OpsTatAppliedFilters = defaultOpsTatAppliedFilters();

  summaryLoading = false;
  inwardLoading = false;
  drilldownLoading = false;
  exportLoading = false;
  summaryError: string | null = null;
  inwardError: string | null = null;
  drilldownError: string | null = null;
  exportError: string | null = null;

  summaryResult: OpsTatSummaryResult | null = null;
  inwardSummaryResult: OpsInwardDevSummaryResult | null = null;
  inwardVendorsResult: OpsInwardDevVendorsResult | null = null;
  drilldownResult: OpsTatDrilldownResult | null = null;

  drawerOpen = false;
  drawerSource: OpsDrawerSource = "tat";
  drawerMetricId: OpsDashboardMetricId | null = null;
  drawerVendorId: number | null = null;
  /** Human card title for GA (e.g. "PR → PO issued"). */
  drawerCardTitle: string | null = null;
  /** Dashboard section for GA (stage_turnaround, days_ledger, …). */
  drawerCardSection: string | null = null;
  drilldownQuery: OpsTatDrilldownQuery = defaultOpsTatDrilldownQuery();

  constructor() {
    this.analyticsAPI = new NewOperationsDashboardAPI();
    makeObservable(this, {
      appliedFilters: observable.ref,
      summaryLoading: observable,
      inwardLoading: observable,
      drilldownLoading: observable,
      exportLoading: observable,
      summaryError: observable,
      inwardError: observable,
      drilldownError: observable,
      exportError: observable,
      summaryResult: observable.ref,
      inwardSummaryResult: observable.ref,
      inwardVendorsResult: observable.ref,
      drilldownResult: observable.ref,
      drawerOpen: observable,
      drawerSource: observable,
      drawerMetricId: observable,
      drawerVendorId: observable,
      drawerCardTitle: observable,
      drawerCardSection: observable,
      drilldownQuery: observable.ref,
      setAppliedFilters: action,
      fetchSummary: action,
      fetchInwardDeviation: action,
      refreshDashboard: action,
      refreshData: action,
      changePath: action,
      openDrawer: action,
      closeDrawer: action,
      fetchDrilldown: action,
      exportCsv: action,
    });
  }

  setAppliedFilters = (filters: OpsTatAppliedFilters) => {
    this.appliedFilters = filters;
  };

  fetchSummary = async (filters?: OpsTatAppliedFilters) => {
    if (filters) {
      this.appliedFilters = filters;
    }
    const inwardPromise = this.fetchInwardDeviation();
    try {
      this.summaryLoading = true;
      this.summaryError = null;

      const body = toOpsAnalyticsFilters(this.appliedFilters);
      const result = await this.analyticsAPI.getSummary(body);

      runInAction(() => {
        this.summaryResult = result;
      });
    } catch (error) {
      runInAction(() => {
        this.summaryError = getApiErrorMessage(
          error,
          "Failed to fetch ops TAT summary.",
        );
        this.summaryResult = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.summaryLoading = false;
      });
      try {
        await inwardPromise;
      } catch {
        // inward error is stored on inwardError
      }
    }
  };

  fetchInwardDeviation = async () => {
    try {
      this.inwardLoading = true;
      this.inwardError = null;

      const body = toOpsAnalyticsFilters(this.appliedFilters);
      const [summary, vendors] = await Promise.all([
        this.analyticsAPI.getInwardDevSummary(body),
        this.analyticsAPI.getInwardDevVendors(body),
      ])

      runInAction(() => {
        this.inwardSummaryResult = summary;
        this.inwardVendorsResult = vendors;
      });
    } catch (error) {
      runInAction(() => {
        this.inwardError = getApiErrorMessage(
          error,
          "Failed to fetch ops inward deviation.",
        );
        this.inwardSummaryResult = null;
        this.inwardVendorsResult = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.inwardLoading = false;
      });
    }
  };

  refreshDashboard = async (filters?: OpsTatAppliedFilters) => {
    try {
      await this.fetchSummary(filters);
      if (this.drawerOpen && this.drawerMetricId) {
        const overrides: Partial<OpsTatDrilldownQuery> = { page: 1 };
        if (this.drawerSource === "inward_dev") {
          overrides.record_scope = this.appliedFilters.include_running
            ? "all"
            : "measured";
        }
        await this.fetchDrilldown(overrides, { silent: true });
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * Re-fetches summary + inward deviation (and the open card, if any) with
   * the already applied filters. Keeps the open card's page / sort / scope.
   */
  refreshData = async () => {
    try {
      if (this.drawerOpen && this.drawerMetricId) {
        trackOpsTatAction("drilldown_refresh", this.appliedFilters, {
          ...this.drawerTrackExtra(),
        });
        const [summary, drilldown] = await Promise.allSettled([
          this.fetchSummary(),
          this.fetchDrilldown(undefined, { silent: true }),
        ]);
        if (summary.status === "rejected") {
          throw summary.reason;
        }
        if (drilldown.status === "rejected") {
          throw drilldown.reason;
        }
        return;
      }
      trackOpsTatAction("dashboard_refresh", this.appliedFilters);
      await this.fetchSummary();
    } catch (error) {
      throw error;
    }
  };

  changePath = async (path: OpsTatPath) => {
    try {
      const previousPath = this.appliedFilters.path;
      if (previousPath === path) {
        return;
      }
      this.appliedFilters = { ...this.appliedFilters, path };
      trackOpsTatAction("path_change", this.appliedFilters, {
        path,
        previousPath,
        context: { path, previous_path: previousPath },
      });
      await this.refreshDashboard();
    } catch (error) {
      throw error;
    }
  };

  openDrawer = async (
    metricId: OpsDashboardMetricId,
    options?: {
      source?: OpsDrawerSource;
      vendorId?: number | null;
      cardTitle?: string;
      cardSection?: string;
    },
  ) => {
    try {
      const source = options?.source ?? "tat";
      const cardTitle = options?.cardTitle?.trim() || null;
      const cardSection = options?.cardSection?.trim() || null;
      trackOpsTatAction("drilldown_open", this.appliedFilters, {
        metricId,
        drawerSource: source,
        vendorId: options?.vendorId,
        cardTitle: cardTitle || undefined,
        cardSection: cardSection || undefined,
        context: {
          metric_id: metricId,
          drawer_source: source,
          vendor_id: options?.vendorId ?? undefined,
          ...(cardTitle ? { card_title: cardTitle } : {}),
          ...(cardSection ? { card_section: cardSection } : {}),
        },
      });
      this.drawerOpen = true;
      this.drawerSource = source;
      this.drawerMetricId = metricId;
      this.drawerVendorId =
        source === "inward_dev" && options?.vendorId != null
          ? options.vendorId
          : null;
      this.drawerCardTitle = cardTitle;
      this.drawerCardSection = cardSection;
      const defaults = defaultOpsTatDrilldownQuery();
      this.drilldownQuery = {
        page: 1,
        page_size:
          source === "inward_dev"
            ? resolveOpsInwardDevPageSize(undefined, metricId)
            : defaults.page_size,
        record_scope:
          source === "inward_dev" && this.appliedFilters.include_running
            ? "all"
            : defaults.record_scope,
      };
      this.drilldownResult = null;
      this.drilldownError = null;
      this.exportError = null;
      await this.fetchDrilldown();
    } catch (error) {
      throw error;
    }
  };

  /**
   * Shared GA extras for the currently open card (title + section + metric).
   *
   * @returns Label/context fields for trackOpsTatAction, or empty when no card.
   */
  private drawerTrackExtra = () => {
    if (!this.drawerMetricId) {
      return {};
    }
    return {
      metricId: this.drawerMetricId,
      drawerSource: this.drawerSource,
      cardTitle: this.drawerCardTitle || undefined,
      cardSection: this.drawerCardSection || undefined,
      vendorId: this.drawerVendorId,
      context: {
        metric_id: this.drawerMetricId,
        drawer_source: this.drawerSource,
        ...(this.drawerCardTitle
          ? { card_title: this.drawerCardTitle }
          : {}),
        ...(this.drawerCardSection
          ? { card_section: this.drawerCardSection }
          : {}),
        ...(this.drawerVendorId != null
          ? { vendor_id: this.drawerVendorId }
          : {}),
      },
    };
  };

  closeDrawer = (options?: { silent?: boolean }) => {
    if (this.drawerOpen && !options?.silent) {
      trackOpsTatAction("drilldown_close", this.appliedFilters, {
        ...this.drawerTrackExtra(),
      });
    }
    this.drawerOpen = false;
    this.drawerSource = "tat";
    this.drawerMetricId = null;
    this.drawerVendorId = null;
    this.drawerCardTitle = null;
    this.drawerCardSection = null;
    this.drilldownResult = null;
    this.drilldownError = null;
    this.exportError = null;
    this.drilldownLoading = false;
    this.exportLoading = false;
    this.drilldownQuery = defaultOpsTatDrilldownQuery();
  };

  private buildDrilldownFilters = () => {
    const filters = toOpsAnalyticsFilters(this.appliedFilters);
    if (this.drawerSource === "inward_dev" && this.drawerVendorId != null) {
      filters.vendor_id = [this.drawerVendorId];
    }
    return filters;
  };

  fetchDrilldown = async (
    overrides?: Partial<OpsTatDrilldownQuery>,
    options?: { silent?: boolean },
  ) => {
    try {
      if (!this.drawerMetricId) {
        return;
      }

      if (overrides && !options?.silent) {
        const prev = this.drilldownQuery;
        const cardExtra = this.drawerTrackExtra();
        if (
          overrides.record_scope !== undefined &&
          overrides.record_scope !== prev.record_scope
        ) {
          trackOpsTatAction("drilldown_scope_change", this.appliedFilters, {
            ...cardExtra,
            recordScope: overrides.record_scope,
            context: {
              ...cardExtra.context,
              record_scope: overrides.record_scope,
            },
          });
        } else if (overrides.sort !== undefined) {
          const nextSort = overrides.sort;
          const prevSort = prev.sort;
          const sortChanged =
            !prevSort ||
            !nextSort ||
            prevSort.column !== nextSort.column ||
            prevSort.dir !== nextSort.dir;
          if (sortChanged) {
            trackOpsTatAction("drilldown_sort", this.appliedFilters, {
              ...cardExtra,
              sortColumn: nextSort.column,
              sortDir: nextSort.dir,
              context: {
                ...cardExtra.context,
                sort_column: nextSort.column,
                sort_dir: nextSort.dir,
              },
            });
          }
        } else if (
          overrides.page_size !== undefined &&
          overrides.page_size !== prev.page_size
        ) {
          trackOpsTatAction("drilldown_page_size_change", this.appliedFilters, {
            ...cardExtra,
            pageSize: overrides.page_size,
            previousPageSize: prev.page_size,
            context: {
              ...cardExtra.context,
              page_size: overrides.page_size,
              previous_page_size: prev.page_size,
            },
          });
        } else if (
          overrides.page !== undefined &&
          overrides.page !== prev.page
        ) {
          trackOpsTatAction("drilldown_page_change", this.appliedFilters, {
            ...cardExtra,
            page: overrides.page,
            pageSize: prev.page_size,
            context: {
              ...cardExtra.context,
              page: overrides.page,
              page_size: prev.page_size,
            },
          });
        }
      }

      this.drilldownQuery = { ...this.drilldownQuery, ...overrides };
      if (this.drawerSource === "inward_dev") {
        this.drilldownQuery = {
          ...this.drilldownQuery,
          page_size: resolveOpsInwardDevPageSize(
            this.drilldownQuery.page_size,
            this.drawerMetricId,
          ),
        };
      }
      this.drilldownLoading = true;
      this.drilldownError = null;

      const body = {
        ...this.buildDrilldownFilters(),
        page: this.drilldownQuery.page,
        page_size: this.drilldownQuery.page_size,
        record_scope: this.drilldownQuery.record_scope,
        ...(this.drilldownQuery.sort ? { sort: this.drilldownQuery.sort } : {}),
      };

      const result =
        this.drawerSource === "inward_dev"
          ? await this.analyticsAPI.getInwardDevDrilldown(
            this.drawerMetricId,
            body,
          )
          : await this.analyticsAPI.getDrilldown(this.drawerMetricId, body);

      runInAction(() => {
        this.drilldownResult = result;
      });
    } catch (error) {
      runInAction(() => {
        this.drilldownError = getApiErrorMessage(
          error,
          this.drawerSource === "inward_dev"
            ? "Failed to fetch ops inward deviation drilldown."
            : "Failed to fetch ops TAT drilldown.",
        );
        this.drilldownResult = null;
      });
      throw error;
    } finally {
      runInAction(() => {
        this.drilldownLoading = false;
      });
    }
  };

  exportCsv = async () => {
    try {
      if (!this.drawerMetricId) {
        return;
      }

      trackOpsTatAction("export_csv", this.appliedFilters, {
        ...this.drawerTrackExtra(),
      });
      this.exportLoading = true;
      this.exportError = null;

      const extras = {
        record_scope: this.drilldownQuery.record_scope,
        sort: this.drilldownQuery.sort,
      };
      const filters = this.buildDrilldownFilters();
      const result =
        this.drawerSource === "inward_dev"
          ? await this.analyticsAPI.exportInwardDevCsv(
            this.drawerMetricId,
            filters,
            extras,
          )
          : await this.analyticsAPI.exportDrilldownCsv(
            this.drawerMetricId,
            filters,
            extras,
          );

      if (!result.data) {
        throw new Error("Download failed");
      }

      const prefix =
        this.drawerSource === "inward_dev" ? "ops-inward-dev" : "ops-tat";
      triggerCsvDownload(
        result.data,
        result.filename || `${prefix}-${this.drawerMetricId}.csv`,
      );
    } catch (error) {
      runInAction(() => {
        this.exportError = getApiErrorMessage(error, "Failed to export CSV.");
      });
      throw error;
    } finally {
      runInAction(() => {
        this.exportLoading = false;
      });
    }
  };
}
